import { createContext, useCallback, useContext, useEffect, useState } from "react";
import i18n from "@/i18n";
import { fetchContent, absoluteMediaUrl } from "@/lib/api";
import { DEFAULT_MEDIA, buildDefaultContentFromI18n } from "@/lib/defaultContent";

const ContentContext = createContext(null);

const applyTranslations = (translations) => {
  if (!translations) return;
  ["tr", "en", "de"].forEach((lng) => {
    if (translations[lng]) {
      i18n.addResourceBundle(lng, "translation", translations[lng], true, true);
    }
  });
  // Re-emit languageChanged so useTranslation consumers refresh
  i18n.changeLanguage(i18n.language);
};

const resolveMedia = (media) => {
  const raw = { ...DEFAULT_MEDIA, ...(media || {}) };
  return Object.fromEntries(
    Object.entries(raw).map(([k, v]) => [k, absoluteMediaUrl(v)])
  );
};

// Apply built-in defaults synchronously so the app renders instantly with
// full content (contact/seo blocks) even when backend is unreachable.
const applyDefaults = () => {
  const def = buildDefaultContentFromI18n(i18n);
  applyTranslations(def.translations);
  return resolveMedia(def.media);
};

export const ContentProvider = ({ children }) => {
  const [media, setMedia] = useState(() => applyDefaults());
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    try {
      const data = await fetchContent();
      applyTranslations(data.translations);
      setMedia(resolveMedia(data.media));
      setVersion((v) => v + 1);
    } catch (e) {
      console.warn("Content fetch failed — using bundled defaults", e?.message);
      // Defaults already applied at init; nothing else to do
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ContentContext.Provider value={{ media, loading, refresh: load, version }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be inside ContentProvider");
  return ctx;
};
